import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card, Spin, Alert, Button, Space, Typography, Tabs, Grid,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { FaChartPie } from 'react-icons/fa6';
import { SettingOutlined, FilePdfOutlined } from '@ant-design/icons';
import PageWrapper from '../../components/common/PageWrapper';
import SupersetDashboard from '../../components/stats/SupersetDashboard.component';
import UserDashboardManagementDrawer from '../../components/crud/UserDashboardManagementDrawer';
import SingleEntitySelect from '../../components/crud/SingleEntitySelect.component';
import { getUserDashboards, listUserDashboards } from '../../actions/userDashboardActions';
import ReportingDashboardPanel from '../../components/reporting/engine/ReportingDashboardPanel';
import ReportingGlobalFilters from '../../components/reporting/engine/ReportingGlobalFilters';
import ReportingDashboardPrintModal from '../../components/reporting/engine/ReportingDashboardPrintModal';
import { resolveApplicationUserIdFromUserInfo } from '../../utils/authUserInfoUtils';
import ApiErrorAlert from '../../components/common/ApiErrorAlert';

const { Paragraph, Text } = Typography;

const TAB_SQL = 'sql';
const TAB_SUPERSET = 'superset';

const ReportingScreen = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const [activeTab, setActiveTab] = useState(TAB_SQL);
  const [dateRange, setDateRange] = useState(null);
  const [cohortId, setCohortId] = useState(null);
  const [uploadBatchId, setUploadBatchId] = useState(null);
  const [manageDashboardsOpen, setManageDashboardsOpen] = useState(false);
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [selectedUserDashboardId, setSelectedUserDashboardId] = useState(null);
  const [selectedSupersetDashboardId, setSelectedSupersetDashboardId] = useState(null);

  const userLogin = useSelector(state => state.userLogin);
  const { userInfo } = userLogin;

  const userDashboardByUser = useSelector(state => state.userDashboardByUser);
  const { dashboards: userDashboards = [], loading: dashboardsLoading, error: dashboardsError } = userDashboardByUser;
  const { items: reportDashboards = [] } = useSelector(state => state.reportDashboardList ?? { items: [] });

  const globalFilters = useMemo(
    () => ({
      cohortId: cohortId ?? undefined,
      uploadBatchId: uploadBatchId ?? undefined,
      organisationId: userInfo?.businessOrganisationId ?? undefined,
    }),
    [cohortId, uploadBatchId, userInfo?.businessOrganisationId],
  );

  const clearGlobalFilters = () => {
    setDateRange(null);
    setCohortId(null);
    setUploadBatchId(null);
  };

  const applicationUserId = resolveApplicationUserIdFromUserInfo(userInfo);

  const organisation = userInfo
    ? {
        supersetBaseUrl: userInfo.supersetBaseUrl,
        supersetDashboardId: userInfo.supersetDashboardId,
        supersetUsername: userInfo.supersetUsername,
        supersetPassword: userInfo.supersetPassword,
      }
    : null;

  const supersetConfigured = Boolean(
    organisation?.supersetBaseUrl && organisation?.supersetDashboardId,
  );

  useEffect(() => {
    document.title = t('Reporting');
    if (applicationUserId) {
      dispatch(getUserDashboards(applicationUserId));
    }
  }, [t, applicationUserId, dispatch]);

  const refreshUserDashboards = () => {
    if (applicationUserId) dispatch(getUserDashboards(applicationUserId));
  };

  const sqlTabContent = (
    <div style={{ paddingTop: isMobile ? 4 : 8 }}>
      <Paragraph type="secondary" style={{ marginBottom: 12, fontSize: isMobile ? 13 : 14 }}>
        {t('Build dashboards from SQL functions on your Data Vault. Charts are scoped to your current organisation. Filters below apply when functions declare optional p_date_from, p_date_to (timestamptz), p_cohort_id, p_upload_batch_id, and p_organisation_id (use SQL Lab templates).')}
      </Paragraph>
      {applicationUserId && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 12,
        }}
        >
          <div style={{ flex: 1, minWidth: 280 }}>
            <ReportingGlobalFilters
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
              cohortId={cohortId}
              onCohortIdChange={setCohortId}
              uploadBatchId={uploadBatchId}
              onUploadBatchIdChange={setUploadBatchId}
              onReset={clearGlobalFilters}
            />
          </div>
          <Button
            icon={<FilePdfOutlined />}
            onClick={() => setPrintModalOpen(true)}
            disabled={reportDashboards.length === 0}
          >
            {t('Print report')}
          </Button>
        </div>
      )}
      {applicationUserId ? (
        <ReportingDashboardPanel
          applicationUserId={applicationUserId}
          dateRange={dateRange}
          globalFilters={globalFilters}
          active={activeTab === TAB_SQL}
        />
      ) : (
        <Alert
          type="warning"
          showIcon
          message={t('SQL dashboards unavailable')}
          description={t('Your login has no application user id. Sign in again or contact an administrator.')}
        />
      )}
    </div>
  );

  const supersetTabContent = (
    <div style={{ paddingTop: isMobile ? 4 : 8 }}>
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'space-between',
        gap: 12,
        marginBottom: 16,
      }}
      >
        <Paragraph type="secondary" style={{ margin: 0, flex: 1, fontSize: isMobile ? 13 : 14 }}>
          {t('Embedded Superset dashboards from your organisation. Link extra dashboard IDs via Manage Superset dashboards.')}
        </Paragraph>
        {supersetConfigured && (
          <Button
            type="default"
            icon={<SettingOutlined />}
            onClick={() => setManageDashboardsOpen(true)}
            block={isMobile}
          >
            {t('Manage Superset dashboards')}
          </Button>
        )}
      </div>

      {supersetConfigured ? (
        <Card
          title={t('Analytics Dashboard')}
          style={{ height: '100%' }}
          styles={{
            header: { position: 'relative', zIndex: 2 },
            body: {
              paddingRight: isMobile ? 16 : 50,
              height: isMobile ? 'min(60vh, 640px)' : 'min(70vh, 900px)',
              minHeight: isMobile ? 360 : 480,
              overflow: 'hidden',
              position: 'relative',
              zIndex: 1,
            },
          }}
          extra={!isMobile && (
            <Space size="middle" style={{ position: 'relative', zIndex: 3 }}>
              <SingleEntitySelect
                singularEntity="userDashboard"
                pluralEntity="userDashboards"
                constantPrefix="USER_DASHBOARD"
                listEntities={listUserDashboards}
                primarySearchProp="name"
                secondarySearchProp="dashboardId"
                placeholder={t('Organization Dashboard')}
                enableCreate={false}
                value={selectedUserDashboardId}
                onChange={(id) => {
                  setSelectedUserDashboardId(id || null);
                  const selected = userDashboards.find(d => d.id === id);
                  setSelectedSupersetDashboardId(selected?.dashboardId || null);
                }}
              />
            </Space>
          )}
        >
          {isMobile && (
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                {t('Organization Dashboard')}
              </Text>
              <SingleEntitySelect
                singularEntity="userDashboard"
                pluralEntity="userDashboards"
                constantPrefix="USER_DASHBOARD"
                listEntities={listUserDashboards}
                primarySearchProp="name"
                secondarySearchProp="dashboardId"
                placeholder={t('Organization Dashboard')}
                enableCreate={false}
                value={selectedUserDashboardId}
                onChange={(id) => {
                  setSelectedUserDashboardId(id || null);
                  const selected = userDashboards.find(d => d.id === id);
                  setSelectedSupersetDashboardId(selected?.dashboardId || null);
                }}
              />
            </div>
          )}
          {dashboardsLoading && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spin />
            </div>
          )}
          <ApiErrorAlert error={dashboardsError} title={t('Error loading dashboards')} />
          <SupersetDashboard
            baseURL={organisation.supersetBaseUrl}
            dashboardId={organisation.supersetDashboardId}
            username={organisation.supersetUsername}
            password={organisation.supersetPassword}
            authProvider={process.env.REACT_APP_SUPERSET_AUTH_PROVIDER}
            first_name={userInfo?.firstName || 'User'}
            last_name={userInfo?.lastName || 'User'}
            selectedDashboardId={selectedSupersetDashboardId}
          />
        </Card>
      ) : (
        <Card>
          <Alert
            type="info"
            message={t('Superset not configured')}
            description={t('Please configure Superset for this organisation to view analytics.')}
            showIcon
          />
        </Card>
      )}
    </div>
  );

  const tabItems = [
    {
      key: TAB_SQL,
      label: isMobile ? t('SQL') : t('SQL dashboards'),
      children: sqlTabContent,
    },
    {
      key: TAB_SUPERSET,
      label: isMobile ? t('Superset') : t('Superset analytics'),
      children: supersetTabContent,
    },
  ];

  return (
    <PageWrapper
      title={t('Reporting')}
      icon={<FaChartPie size={24} />}
      showCard={false}
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        defaultActiveKey={TAB_SQL}
        items={tabItems}
        size={isMobile ? 'small' : 'middle'}
        tabBarGutter={isMobile ? 8 : 24}
        tabBarStyle={{
          marginBottom: isMobile ? 12 : 16,
          overflowX: 'auto',
          overflowY: 'hidden',
          flexWrap: 'nowrap',
        }}
        style={{ width: '100%' }}
        destroyInactiveTabPane={false}
      />

      <UserDashboardManagementDrawer
        open={manageDashboardsOpen}
        onClose={() => {
          setManageDashboardsOpen(false);
          refreshUserDashboards();
        }}
        applicationUserId={applicationUserId}
      />

      <ReportingDashboardPrintModal
        open={printModalOpen}
        onClose={() => setPrintModalOpen(false)}
        dashboards={reportDashboards}
        dateRange={dateRange}
        globalFilters={globalFilters}
      />
    </PageWrapper>
  );
};

export default ReportingScreen;
